import Stack from '@mui/material/Stack';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import Checkbox from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';
import { OptionalTooltip } from 'common/components/Tooltip/OptionalTooltip';
import { styled } from '@mui/material/styles';
import { pxToRem } from 'src/common/utils/fonts';
import { getOptionNameOverflowDimensions } from 'src/common/components/Inputs/Autocomplete/Components/Options/utils/getOptionNameOverflowDimensions';

const StyledCheckbox = styled(Checkbox)({
  padding: '0',
  '& svg': { fontSize: pxToRem(24) },
  pointerEvents: 'auto',
});

const OptionLabelDisplayRoot = styled(Typography)({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: 'inline-block',
  whiteSpace: 'nowrap',
  '&:has(+ span)::after': {
    content: '", "',
  },
});

const CheckBoxEmptyIcon = ({ disabled }: { disabled?: boolean }) => {
  return (
    <CheckBoxOutlineBlankIcon
      fontSize="small"
      sx={{ color: (theme) => (disabled ? theme.palette.border : theme.palette.text.secondary) }}
    />
  );
};

const CheckedIcon = () => (
  <CheckBoxIcon fontSize="small" sx={{ color: (theme) => theme.palette.text.secondary }} />
);

type TextOptionProps = {
  id?: string;
  name: string;
  selected: boolean;
  disabled?: boolean;
  disabledInfo?: string;
  labelTextColor?: string;
  checkbox?: boolean;
};

export const TextOption = ({
  id,
  name,
  selected,
  disabled,
  disabledInfo,
  labelTextColor = 'text.primary',
  checkbox = true,
}: TextOptionProps) => {
  const displayTooltip = !!(disabled && disabledInfo);

  return (
    <Stack
      direction="row"
      alignItems="center"
      alignContent="center"
      spacing={2}
      data-testid="multiselect-option"
    >
      {checkbox && (
        <OptionalTooltip
          title={disabledInfo}
          showArrow={true}
          variant="dark"
          hide={!displayTooltip}
        >
          <span data-testid="checkbox-icon">
            <StyledCheckbox
              icon={<CheckBoxEmptyIcon disabled={disabled} />}
              checkedIcon={<CheckedIcon />}
              checked={selected}
              disabled={disabled}
            />
          </span>
        </OptionalTooltip>
      )}

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <OptionalTooltip
          title={name}
          showArrow={true}
          variant="dark"
          hide={name.length < getOptionNameOverflowDimensions(!!id).maxNumberOfCharacters}
        >
          <OptionLabelDisplayRoot
            variant="text1"
            color={labelTextColor}
            sx={{ maxWidth: getOptionNameOverflowDimensions(!!id).nameWidth }}
          >
            {name}
          </OptionLabelDisplayRoot>
        </OptionalTooltip>
        {id && (
          <Typography variant="text1" color="text.secondary">
            {id}
          </Typography>
        )}
      </div>
    </Stack>
  );
};
